import type { TurnWorld, Scenario, Building, Unit, PlayerState, PlayerId, TileMap } from './types';
import { UNIT_DEFS } from './units';

export interface LoadedScenario {
  world: TurnWorld;
  tagMap: Map<string, number>;
}

/** Create a TurnWorld from a Scenario definition. */
export function loadScenario(scenario: Scenario): LoadedScenario {
  const map: TileMap = {
    width: scenario.width,
    height: scenario.height,
    tiles: scenario.terrain.map((row) => row.map((type) => ({ type }))),
  };
  // Apply sparse per-tile flag overrides (no-overlay road/bridge, impassable).
  for (const o of scenario.tileOverrides ?? []) {
    const tile = map.tiles[o.row]?.[o.col];
    if (!tile) continue;
    if (o.noOverlay) tile.noOverlay = true;
    if (o.impassable) tile.impassable = true;
    if (o.quay) tile.quay = true;
  }

  const players: PlayerState[] = scenario.players.map((id) => ({
    id,
    gold: scenario.startingGold,
    alive: true,
  }));

  let nextId = 1;
  const tagMap = new Map<string, number>();

  const buildings = new Map<number, Building>();
  for (const sb of scenario.buildings) {
    const b: Building = {
      id: nextId++,
      type: sb.type,
      owner: sb.owner,
      col: sb.col,
      row: sb.row,
      captureProgress: 0,
      capturingPlayer: null,
      training: null,
      buildProgress: 0,
      buildingPlayerId: null,
    };
    buildings.set(b.id, b);
    if (sb.tag) tagMap.set(sb.tag, b.id);
  }

  const units = new Map<number, Unit>();
  for (const su of scenario.units) {
    const u: Unit = {
      id: nextId++,
      type: su.type,
      owner: su.owner,
      col: su.col,
      row: su.row,
      hp: su.hp ?? UNIT_DEFS[su.type].maxHp,
      acted: false,
      movePoints: UNIT_DEFS[su.type].movementRange,
      fuel: su.fuel ?? UNIT_DEFS[su.type].maxFuel,
      ammo: su.ammo ?? UNIT_DEFS[su.type].maxAmmo,
      xp: 0,
    };
    // Seed starting cargo for carriers (e.g. aircraft on a carrier's deck).
    // The carried unit's owner is inherited from the carrier; HP defaults to
    // the cargo type's maxHp. Validation guarantees capacity/accept rules.
    if (su.cargo && su.cargo.length > 0) {
      u.cargo = su.cargo.map((c) => ({
        type: c.type,
        hp: c.hp ?? UNIT_DEFS[c.type].maxHp,
        owner: su.owner,
        acted: false,
        xp: 0,
      }));
    }
    units.set(u.id, u);
    if (su.tag) tagMap.set(su.tag, u.id);
  }

  const explored: Set<string>[] = scenario.players.map(() => new Set<string>());

  // Scenario-forced factions: populate world.factions from the scenario.
  // These take precedence over menu picks (TurnGameScene fills in the rest).
  let factions: Map<PlayerId, string> | undefined;
  if (scenario.factions) {
    factions = new Map<PlayerId, string>();
    for (const [key, factionId] of Object.entries(scenario.factions)) {
      factions.set(Number(key), factionId);
    }
  }

  const world: TurnWorld = {
    map,
    buildings,
    units,
    players,
    turnOrder: scenario.players,
    currentPlayerIndex: 0,
    turnNumber: 0, // startGame will set to 1
    phase: 'income',
    nextId,
    humanPlayer: 0, // overridden by TurnGameScene.create() from GameConfig
    explored,
    factions, // scenario-forced; menu picks fill remaining slots in TurnGameScene
  };

  return { world, tagMap };
}
